import { sectionLabels } from "../section-labels";
import { colorFields } from "./_shared";
/**
 * Before / after — a draggable split-image comparison. The seller supplies two
 * photos of the same subject; the visitor wipes a handle across the frame to
 * reveal one under the other.
 *
 * Ported from the EasyOrders `ab-comparison-section`, which 8 of their 13 theme
 * engines ship (see docs/plans/2026-07-19-storefront-section-vocabulary.md).
 * Common uses: a product's result (skincare, supplements), a restoration or
 * install, a colourway swap.
 */
export const beforeAfterSchema = {
    type: "before-after",
    name: sectionLabels["before-after"],
    description: {
        ar: "مقارنة بين صورتين بمقبض يتسحب لكشف الفرق.",
        en: "Two images compared with a draggable handle.",
    },
    maxPerTemplate: 2,
    // Furniture, not a menu item: it ships in 13 of the 14 presets — one per
    // family, every family but `default` — and in none of the demo stores. It is
    // here because the storefronts we ported had it, and it needs two photos of
    // the same subject that most sellers do not have. Every shipped instance
    // still renders and still edits; the add menu just stops offering it.
    offeredInAddMenu: false,
    settings: [
        {
            id: "variant",
            type: "select",
            ui: "thumbnail",
            label: { ar: "نوع العرض", en: "Layout" },
            helpText: {
                ar: "طريقة عرض المقارنة.",
                en: "How the comparison is laid out.",
            },
            options: [
                { value: "slider", label: { ar: "سحب", en: "Slider" } },
                { value: "panel", label: { ar: "لوحة عريضة", en: "Panel" } },
            ],
            default: "slider",
        },
        {
            id: "eyebrow",
            type: "text",
            i18n: true,
            label: { ar: "سطر فوق العنوان", en: "Eyebrow" },
            helpText: {
                ar: "سطر صغير بحروف كبيرة فوق العنوان.",
                en: "A small spaced line above the heading.",
            },
            validate: { max: 40 },
            showIf: { variant: ["panel"] },
        },
        {
            id: "title",
            type: "text",
            i18n: true,
            label: { ar: "العنوان", en: "Title" },
            validate: { max: 80 },
            default: { ar: "شوف الفرق", en: "See the transformation" },
        },
        {
            id: "description",
            type: "textarea",
            i18n: true,
            label: { ar: "الوصف", en: "Description" },
            validate: { max: 160 },
        },
        {
            id: "beforeImage",
            type: "image",
            label: { ar: "صورة قبل", en: "Before image" },
            helpText: {
                ar: "الصورة اللي بتتكشف أول ما المقبض يتحرك.",
                en: "The image the handle wipes away.",
            },
        },
        {
            id: "afterImage",
            type: "image",
            label: { ar: "صورة بعد", en: "After image" },
            helpText: {
                ar: "الصورة اللي بتظهر تحت.",
                en: "The image revealed underneath.",
            },
        },
        {
            id: "orientation",
            type: "select",
            label: { ar: "اتجاه السحب", en: "Drag direction" },
            options: [
                { value: "horizontal", label: { ar: "أفقي", en: "Horizontal" } },
                { value: "vertical", label: { ar: "رأسي", en: "Vertical" } },
            ],
            default: "horizontal",
        },
        {
            id: "shape",
            type: "select",
            label: { ar: "شكل الإطار", en: "Frame shape" },
            helpText: {
                ar: "مربع بنسبة 16:9، أو دائرة بنسبة 1:1.",
                en: "Square is 16:9; circle crops to 1:1.",
            },
            options: [
                { value: "square", label: { ar: "مربع", en: "Square" } },
                { value: "circle", label: { ar: "دائرة", en: "Circle" } },
            ],
            default: "square",
            showIf: { variant: ["slider"] },
        },
        {
            // `panel` is full-bleed, so its frame is set by ratio rather than shape.
            // The four values are the source's own `--aspect-*` shell modifiers.
            id: "aspect",
            type: "select",
            label: { ar: "نسبة الإطار", en: "Frame ratio" },
            options: [
                { value: "ultrawide", label: { ar: "عريض جدًا 21:9", en: "Ultrawide 21:9" } },
                { value: "panorama", label: { ar: "بانورامي 21:11", en: "Panorama 21:11" } },
                { value: "wide", label: { ar: "عريض 16:9", en: "Wide 16:9" } },
                { value: "squareish", label: { ar: "قريب من المربع 4:3", en: "Squareish 4:3" } },
            ],
            default: "ultrawide",
            showIf: { variant: ["panel"] },
        },
        {
            id: "initialPosition",
            type: "range",
            min: 0,
            max: 100,
            step: 5,
            label: { ar: "موضع المقبض", en: "Starting position" },
            helpText: {
                ar: "نسبة صورة «قبل» الظاهرة عند فتح الصفحة.",
                en: "How much of the before image shows on load.",
            },
            default: 50,
        },
        {
            id: "showLabels",
            type: "checkbox",
            label: { ar: "إظهار كلمتي قبل/بعد", en: "Show before / after labels" },
            default: true,
        },
        {
            id: "beforeLabel",
            type: "text",
            i18n: true,
            label: { ar: "كلمة «قبل»", en: '"Before" label' },
            validate: { max: 24 },
            showIf: { showLabels: true },
            default: { ar: "قبل", en: "before" },
        },
        {
            id: "afterLabel",
            type: "text",
            i18n: true,
            label: { ar: "كلمة «بعد»", en: '"After" label' },
            validate: { max: 24 },
            showIf: { showLabels: true },
            default: { ar: "بعد", en: "after" },
        },
        ...colorFields(),
    ],
};
//# sourceMappingURL=before-after.js.map